import {
  Badge,
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Group,
  SimpleGrid,
  Card,
  LoadingOverlay,
  Alert,
  Select,
  TextInput,
  Button,
  SegmentedControl,
  Box,
  Tooltip,
  ThemeIcon,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { LineChart, BarChart } from "@mantine/charts";
import {
  IconInfoCircle,
  IconChartLine,
  IconChartBar,
  IconCalendar,
  IconFilter,
  IconSettings,
  IconEye,
} from "@tabler/icons-react";
import { useLocation } from "wouter";
import { useBrandsStore } from "./brandsStore";
import { useLlmProvidersStore } from "../LlmProviders/llmProvidersStore";
import { providerFilterValue } from "../LlmProviders/googleSurfaces";
import { jsonGet } from "../api";
import { MetricDelta } from "../_Shared/metricDelta";

export default function BrandAnalyticsTimeline({ brand }) {
  const {
    fetchAnalyticsTimeline,
    getCachedAnalyticsTimeline,
    isLoadingAnalytics,
  } = useBrandsStore();
  const { llmProviders } = useLlmProvidersStore();
  const [, setLocation] = useLocation();

  const [timelineData, setTimelineData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);
  const [categorySummaries, setCategorySummaries] = useState([]);
  const [llmTraffic, setLlmTraffic] = useState({});

  // Filter state
  const [filters, setFilters] = useState({
    industry: "",
    aspect: "",
    type: "",
    llmProvider: "",
    startDate: null,
    endDate: null,
  });

  // UI state
  const [chartType, setChartType] = useState("line"); // 'line' or 'bar'
  const [selectedMetric, setSelectedMetric] = useState("averagePosition");

  const loadData = async (currentFilters) => {
    if (!brand) return;

    try {
      setError(null);

      // Check cache first
      const cachedData = getCachedAnalyticsTimeline(brand.id, currentFilters);
      if (cachedData) {
        setTimelineData(cachedData.timePoints);
        setTotalCount(cachedData.totalCount);
        return;
      }

      // Fetch fresh data
      const data = await fetchAnalyticsTimeline(brand.id, currentFilters);
      setTimelineData(data?.timePoints || []);
      setTotalCount(data?.totalCount || 0);
    } catch (err) {
      // Don't treat filtered empty results as errors
      if (err.message?.includes("404")) {
        setTimelineData([]);
        setTotalCount(0);
      } else {
        setError(err.message);
      }
    }
  };

  useEffect(() => {
    if (brand?.id) {
      loadData(filters);
      jsonGet(`brands/${brand.id}/awareness/analytics/category-summaries`)
        .then(setCategorySummaries)
        .catch(() => setCategorySummaries([]));

      // Fetch LLM-referred traffic for overlay
      const from = new Date(Date.now() - 30 * 86400000).toISOString();
      const to = new Date().toISOString();
      jsonGet(`traffic?projectId=${brand.id}&trafficSource=llm&from=${from}&to=${to}&pageSize=1000`)
        .then((result) => {
          // Group by week to align with monitoring runs
          const byWeek = {};
          (result?.data || []).forEach((evt) => {
            const d = new Date(evt.timestamp);
            const weekKey = new Date(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay()).toLocaleDateString();
            byWeek[weekKey] = (byWeek[weekKey] || 0) + 1;
          });
          setLlmTraffic(byWeek);
        })
        .catch(() => setLlmTraffic({}));
    }
  }, [brand]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    loadData(filters);
  };

  const clearFilters = () => {
    setFilters({
      industry: "",
      aspect: "",
      type: "",
      llmProvider: "",
      startDate: null,
      endDate: null,
    });
  };

  // Prepare chart data based on selected metric
  const getChartData = () => {
    if (!timelineData?.length) return [];

    return timelineData.map((point) => {
      const ts = new Date(point.timestamp);
      const weekKey = new Date(ts.getFullYear(), ts.getMonth(), ts.getDate() - ts.getDay()).toLocaleDateString();
      return {
        timestamp: ts.toLocaleDateString(),
        averagePosition: point.analytics.averagePosition > 0 ? point.analytics.averagePosition : null,
        totalMentions: point.analytics.totalMentions,
        sentimentScore: calculateAverageSentiment(
          point.analytics.sentimentScoreDistribution
        ),
        shareOfVoice: calculateAverageShareOfVoice(
          point.analytics.shareOfVoiceDistribution
        ),
        mentionRank: calculateAverageMentionRank(
          point.analytics.mentionRankDistribution
        ),
        recommendationRate: (point.analytics.recommendationRate ?? 0) * 100,
        llmVisits: llmTraffic[weekKey] || 0,
        methodologyVersion: point.methodologyVersion ?? 0,
      };
    });
  };

  // Helper functions to calculate averages from distributions
  const calculateAverageSentiment = (distribution) => {
    if (!distribution?.length) return 0;
    const total = distribution.reduce((sum, bucket) => sum + bucket.count, 0);
    if (total === 0) return 0;
    const weightedSum = distribution.reduce((sum, bucket) => {
      const midpoint = (bucket.rangeStart + bucket.rangeEnd) / 2;
      return sum + midpoint * bucket.count;
    }, 0);
    return weightedSum / total;
  };

  const calculateAverageShareOfVoice = (distribution) => {
    if (!distribution?.length) return 0;
    const total = distribution.reduce((sum, bucket) => sum + bucket.count, 0);
    if (total === 0) return 0;
    const weightedSum = distribution.reduce((sum, bucket) => {
      const midpoint = (bucket.rangeStart + bucket.rangeEnd) / 2;
      return sum + midpoint * bucket.count;
    }, 0);
    return weightedSum / total;
  };

  const calculateAverageMentionRank = (distribution) => {
    if (!distribution?.length) return 0;
    const total = distribution.reduce((sum, bucket) => sum + bucket.count, 0);
    if (total === 0) return 0;
    const weightedSum = distribution.reduce((sum, bucket) => {
      const midpoint = (bucket.rangeStart + bucket.rangeEnd) / 2;
      return sum + midpoint * bucket.count;
    }, 0);
    return weightedSum / total;
  };

  const chartData = getChartData();

  // Points either side of a methodology change measured DIFFERENT models, so the step between
  // them is our instrument moving, not the brand's visibility. Say so rather than letting the
  // line imply a trend across it.
  const methodologyChangedAt = chartData.find(
    (point, i) => i > 0 && point.methodologyVersion !== chartData[i - 1].methodologyVersion
  )?.timestamp;

  const metricOptions = [
    {
      value: "averagePosition",
      label: "Average Position",
      reversed: true,
      domain: [1, 10],
      formatter: (value) => (value > 0 ? `${value.toFixed(1)}` : "—"),
    },
    {
      value: "totalMentions",
      label: "Total Mentions",
      formatter: (value) => `${value.toFixed(0)}`,
    },
    {
      value: "sentimentScore",
      label: "Sentiment Score",
      formatter: (value) => `${value.toFixed(1)}`,
    },
    {
      value: "shareOfVoice",
      label: "Share of Voice",
      formatter: (value) => `${value.toFixed(1)}%`,
    },
    {
      value: "mentionRank",
      label: "Mention Rank",
      reversed: true,
      domain: [1, 10],
      formatter: (value) => `${value.toFixed(1)}`,
    },
    {
      value: "recommendationRate",
      label: "Recommendation Rate",
      domain: [0, 100],
      formatter: (value) => `${value.toFixed(0)}%`,
    },
    {
      value: "llmVisits",
      label: "LLM-Referred Visits",
      formatter: (value) => `${value.toFixed(0)}`,
    },
  ];

  const selectedMetricObj =
    metricOptions.find((opt) => opt.value === selectedMetric) ||
    metricOptions[0];

  if (isLoadingAnalytics) {
    return (
      <Container size="xl">
        <LoadingOverlay visible={true} />
      </Container>
    );
  }

  const hasActiveFilters = filters.industry || filters.aspect || filters.type || filters.llmProvider || filters.startDate || filters.endDate;

  if (error) {
    return (
      <Container size="xl">
        <Alert icon={<IconInfoCircle />} title="Error" color="red">
          {error}
        </Alert>
      </Container>
    );
  }

  if (!timelineData?.length && !isLoadingAnalytics && !hasActiveFilters) {
    return (
      <Stack gap="md" align="center" py="xl" mt="md">
        <ThemeIcon size={60} radius="xl" variant="light" color="blue">
          <IconEye size={30} />
        </ThemeIcon>
        <Title order={3}>No AI Visibility Data Yet</Title>
        <Text c="dimmed" ta="center" maw={500}>
          AI Visibility tracks how your brand appears across LLM providers like ChatGPT, Claude, and Gemini.
          Set up monitors to start collecting visibility data.
        </Text>
        <Button
          variant="light"
          leftSection={<IconSettings size={16} />}
          onClick={() => setLocation("/llm-config")}
        >
          Configure Monitors
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap="sm" mt="md">
      {/* Summary Cards */}
      {timelineData?.length > 0 && (
        <SimpleGrid cols={5}>
          <Paper withBorder p="sm">
            <Text size="xs" c="dimmed">
              Data Points
            </Text>
            <Text ta="right" size="lg" fw={700}>
              {totalCount}
            </Text>
          </Paper>

          <Paper withBorder p="sm">
            <Text size="xs" c="dimmed">
              Time Slices
            </Text>
            <Text ta="right" size="lg" fw={700}>
              {timelineData.length}
            </Text>
          </Paper>

          <Paper withBorder p="sm">
            <Text size="xs" c="dimmed">
              Date Range
            </Text>
            <Text ta="right" fz="xs" fw={500}>
              {new Date(timelineData[0]?.timestamp).toLocaleDateString()} -{" "}
              {new Date(
                timelineData[timelineData.length - 1]?.timestamp
              ).toLocaleDateString()}
            </Text>
          </Paper>

          <Paper withBorder p="sm">
            <Text size="xs" c="dimmed">
              Avg Position
            </Text>
            <Text ta="right" size="lg" fw={700}>
              {chartData.length > 0
                ? chartData.filter((point) => point.averagePosition > 0)
                    .length > 0
                  ? (
                      chartData
                        .filter((point) => point.averagePosition > 0)
                        .reduce(
                          (sum, point) => sum + point.averagePosition,
                          0
                        ) /
                      chartData.filter((point) => point.averagePosition > 0)
                        .length
                    ).toFixed(1)
                  : "—"
                : "—"}
            </Text>
          </Paper>

          <Paper withBorder p="sm">
            <Text size="xs" c="dimmed">
              Total Mentions
            </Text>
            <Text ta="right" size="lg" fw={700}>
              {chartData.reduce((sum, point) => sum + point.totalMentions, 0)}
            </Text>
          </Paper>
        </SimpleGrid>
      )}

      {/* Category Summaries */}
      {categorySummaries?.length > 0 && (
        <Paper p="md" withBorder>
          <Text fw={600} mb="sm">
            Recommendation Performance by Category
          </Text>
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }}>
            {categorySummaries.map((cat) => (
              <Card
                key={cat.category}
                withBorder
                p="sm"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  handleFilterChange("industry", cat.category);
                  handleFilterChange("type", "ProductRecommendation");
                  loadData({
                    ...filters,
                    industry: cat.category,
                    type: "ProductRecommendation",
                  });
                }}
              >
                <Text size="sm" fw={600} truncate>
                  {cat.category}
                </Text>
                <Group gap="xs" mt={4}>
                  <Text size="lg" fw={700} c={cat.recommendationRate > 0.5 ? "green" : cat.recommendationRate > 0.2 ? "yellow" : "red"}>
                    {(cat.recommendationRate * 100).toFixed(0)}%
                  </Text>
                  <Text size="xs" c="dimmed">recommended</Text>
                  {/* cat.trend is a recommendation-RATE delta, so it reads in points, not percent. */}
                  <MetricDelta metric="recommendationRate" value={cat.trend} hideWhenFlat />
                </Group>
                {cat.avgRecommendationRank > 0 && (
                  <Text size="xs" c="dimmed">
                    Avg rank: #{cat.avgRecommendationRank.toFixed(1)}
                  </Text>
                )}
              </Card>
            ))}
          </SimpleGrid>
        </Paper>
      )}

      {/* Category Quick Filters */}
      {categorySummaries?.length > 0 && (
        <Group gap="xs">
          <Text size="sm" fw={500} c="dimmed">Category:</Text>
          <Badge
            variant={!filters.aspect ? "filled" : "light"}
            color="blue"
            style={{ cursor: "pointer" }}
            onClick={() => {
              const next = { ...filters, aspect: "", industry: "" };
              setFilters(next);
              loadData(next);
            }}
          >
            All
          </Badge>
          {categorySummaries.map((cat) => (
            <Badge
              key={cat.category}
              variant={filters.aspect === cat.category ? "filled" : "light"}
              color="blue"
              style={{ cursor: "pointer" }}
              onClick={() => {
                const next = { ...filters, aspect: cat.category, industry: "" };
                setFilters(next);
                loadData(next);
              }}
            >
              {cat.category}
            </Badge>
          ))}
        </Group>
      )}

      {/* Chart Controls */}
      <Paper p="xs" withBorder>
        <Group justify="space-between" align="center">
          <SegmentedControl
            size="sm"
            value={chartType}
            onChange={setChartType}
            data={[
              { label: "Line Chart", value: "line" },
              { label: "Bar Chart", value: "bar" },
            ]}
          />
          <Title order={4}>
            <Group>
              {chartType === "line" ? (
                <IconChartLine size={20} />
              ) : (
                <IconChartBar size={20} />
              )}
              <Text>Analytics Chart</Text>
            </Group>
          </Title>

          <Group>
            Metric
            <Select
              size="sm"
              data={metricOptions}
              value={selectedMetric}
              onChange={setSelectedMetric}
              w={200}
            />
          </Group>
        </Group>
      </Paper>

      {/* Main Chart */}
      <Paper p="md" withBorder>
        {methodologyChangedAt && (
          <Alert color="gray" variant="light" mb="sm" icon={<IconInfoCircle size={16} />}>
            We changed which models we probe on {methodologyChangedAt}. Figures before and after
            that date measure different models and are not directly comparable.
          </Alert>
        )}
        {chartData.length > 0 ? (
          <Box h={200}>
            {chartType === "line" ? (
              <LineChart
                h={200}
                data={chartData}
                dataKey="timestamp"
                series={[
                  {
                    name: selectedMetric,
                    color: "blue.6",
                    label: selectedMetricObj.label,
                  },
                ]}
                yAxisProps={{
                  reversed: selectedMetricObj.reversed, // Lower position numbers should be at top
                  domain: selectedMetricObj.domain,
                }}
                valueFormatter={selectedMetricObj.formatter}
                curveType="linear"
                withLegend
                legendProps={{ verticalAlign: "top" }}
                withDots
                dotProps={{ strokeWidth: 2, r: 4 }}
                activeDotProps={{ strokeWidth: 2, r: 6 }}
              />
            ) : (
              <BarChart
                h={200}
                data={chartData}
                dataKey="timestamp"
                series={[
                  {
                    name: selectedMetric,
                    color: "blue.6",
                    label: selectedMetricObj.label,
                  },
                ]}
                withLegend
                legendProps={{ verticalAlign: "top" }}
                valueFormatter={selectedMetricObj.formatter}
              />
            )}
          </Box>
        ) : (
          <Box
            h={350}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text c="dimmed">No data available for the selected filters</Text>
          </Box>
        )}
      </Paper>

      {/* Distribution Charts */}
      {timelineData?.length > 0 && (
        <SimpleGrid cols={1} spacing="md">
          <Paper p="md" withBorder>
            <Title order={4} mb="md">
              Latest Distribution Analysis
            </Title>

            {timelineData[timelineData.length - 1]?.analytics && (
              <SimpleGrid cols={3} spacing="lg">
                {/* Sentiment Distribution */}
                <Box>
                  <Text fw={600} mb="xs">
                    Sentiment Score Distribution
                    <Tooltip
                      w="35%"
                      multiline
                      label="The distribution of sentiment scores across mentions. A higher score indicates more positive sentiment. The height of the bars is the total count of mentions with that sentiment score range. You want to see high bars on the right side of the chart (positive sentiments) and ideally none on the left (negative sentiments)."
                    >
                      <IconInfoCircle size={16} />
                    </Tooltip>
                  </Text>
                  <BarChart
                    h={200}
                    data={
                      timelineData[
                        timelineData.length - 1
                      ].analytics.sentimentScoreDistribution?.map((bucket) => ({
                        // range: /*bucket.label || */ `${bucket.rangeStart.toFixed(
                        //   1
                        // )}-${bucket.rangeEnd.toFixed(1)}`,
                        range:
                          bucket.rangeStart < 0
                            ? `< ${bucket.rangeEnd.toFixed(1)}`
                            : `> ${bucket.rangeStart.toFixed(1)}`,
                        count: bucket.count,
                      })) || []
                    }
                    dataKey="range"
                    series={[{ name: "count", color: "green.6" }]}
                  />
                </Box>

                {/* Share of Voice Distribution */}
                <Box>
                  <Text fw={600} mb="xs">
                    Share of Voice Distribution
                    <Tooltip
                      w="35%"
                      multiline
                      label="The distribution of share of voice across mentions. A higher percentage indicates a greater share of voice. The height of the bars is the total count of mentions within that share of voice range. You want to see high bars on the right side of the chart (higher share of voice). An overly high share of voice (e.g., > 50%) may indicate a brand is dominating the conversation, which can be an indicator of too narrow prompts."
                    >
                      <IconInfoCircle size={16} />
                    </Tooltip>
                  </Text>
                  <BarChart
                    h={200}
                    data={
                      timelineData[
                        timelineData.length - 1
                      ].analytics.shareOfVoiceDistribution?.map((bucket) => ({
                        //range: /*bucket.label ||*/ `${bucket.rangeStart.toFixed(0)}-${bucket.rangeEnd.toFixed(0)}`,
                        range: `≤ ${bucket.rangeEnd.toFixed(0)}%`,
                        count: bucket.count,
                      })) || []
                    }
                    dataKey="range"
                    series={[{ name: "count", color: "blue.6" }]}
                  />
                </Box>

                {/* Mention Rank Distribution */}
                <Box>
                  <Text fw={600} mb="xs">
                    Mention Rank Distribution
                    <Tooltip
                      w="35%"
                      multiline
                      label="The distribution of mention ranks across mentions. A lower rank indicates a more prominent mention. The height of the bars is the total count of mentions within that rank. You want to see high bars on the left side of the chart (lower ranks means you get mentioned earlier)."
                    >
                      <IconInfoCircle size={16} />
                    </Tooltip>
                  </Text>
                  <BarChart
                    h={200}
                    data={
                      timelineData[
                        timelineData.length - 1
                      ].analytics.mentionRankDistribution?.map((bucket) => ({
                        //range: /*bucket.label ||*/ `${bucket.rangeStart.toFixed(0)}-${bucket.rangeEnd.toFixed(0)}`,
                        range: `${bucket.rangeStart.toFixed(0)}`,
                        count: bucket.count,
                      })) || []
                    }
                    dataKey="range"
                    series={[{ name: "count", color: "orange.6" }]}
                  />
                </Box>
              </SimpleGrid>
            )}
          </Paper>
        </SimpleGrid>
      )}

      {/* Filters */}
      <Paper p="md" withBorder>
        <Group justify="space-between" align="flex-start" mb="sm">
          <Title order={4} mb="md">
            <Group>
              <IconFilter size={20} />
              <Text>Filters</Text>
            </Group>
          </Title>

          <SegmentedControl
            size="sm"
            data={[
              { value: "", label: "All Types" },
              { value: "unaidedAwareness", label: "Unaided Awareness" },
              { value: "reputation", label: "Reputation (aided)" },
              { value: "ProductRecommendation", label: "Recommendations" },
              { value: "custom", label: "Custom" },
            ]}
            value={filters.type}
            onChange={(e) => handleFilterChange("type", e)}
          />
        </Group>

        <SimpleGrid cols={3} spacing="sm">
          <Select
            label="Industry"
            placeholder="e.g., Technology"
            value={filters.industry}
            onChange={(e) => handleFilterChange("industry", e)}
            data={brand?.industries || []}
          />

          <Select
            searchable
            clearable
            label="Category / Aspect"
            placeholder="e.g., Hiking Boots"
            value={filters.aspect}
            onChange={(e) => handleFilterChange("aspect", e)}
            data={[
              ...(categorySummaries?.map((cat) => ({
                value: cat.category,
                label: cat.category,
              })) || []),
              ...(brand?.topics?.filter((t) => !categorySummaries?.some((c) => c.category === t.name))
                .map((topic) => ({
                  value: topic.name,
                  label: topic.name,
                })) || []),
            ]}
          />

          <Select
            searchable
            clearable
            data={
              llmProviders?.map((provider) => ({
                // Google surfaces filter by a stable string, not their document id.
                value: providerFilterValue(provider),
                label: provider.name,
              })) || []
            }
            label="LLM Provider"
            placeholder="e.g., OpenAI"
            value={filters.llmProvider}
            onChange={(e) => handleFilterChange("llmProvider", e)}
          />
        </SimpleGrid>

        <SimpleGrid cols={3} spacing="md" mt="md">
          <TextInput
            label="Start Date"
            placeholder="YYYY-MM-DD"
            type="date"
            value={
              filters.startDate
                ? filters.startDate.toISOString().split("T")[0]
                : ""
            }
            onChange={(e) =>
              handleFilterChange(
                "startDate",
                e.target.value ? new Date(e.target.value) : null
              )
            }
            leftSection={<IconCalendar size={16} />}
          />

          <TextInput
            label="End Date"
            placeholder="YYYY-MM-DD"
            type="date"
            value={
              filters.endDate ? filters.endDate.toISOString().split("T")[0] : ""
            }
            onChange={(e) =>
              handleFilterChange(
                "endDate",
                e.target.value ? new Date(e.target.value) : null
              )
            }
            leftSection={<IconCalendar size={16} />}
          />

          <Group align="flex-end" h="100%">
            <Button
              onClick={applyFilters}
              loading={isLoadingAnalytics}
              variant="filled"
            >
              Apply Filters
            </Button>
            <Button onClick={clearFilters} variant="light">
              Clear
            </Button>
          </Group>
        </SimpleGrid>
      </Paper>

      {/* Top Competitors */}
      {timelineData?.length > 0 &&
        timelineData[timelineData.length - 1]?.analytics?.topCompetitors
          ?.length > 0 && (
          <Paper p="md" withBorder>
            <Title order={4} mb="md">
              Top Brands (Latest)
            </Title>
            <SimpleGrid cols={4} spacing="md">
              {timelineData[timelineData.length - 1].analytics.topCompetitors
                .slice(0, 8)
                .map((competitor, index) => (
                  <Card key={competitor.brandName} withBorder>
                    <Group wrap="nowrap" justify="space-between">
                      <Text fw={500} truncate>
                        {competitor.brandName}
                      </Text>
                      <Text size="sm" c="dimmed">
                        #{index + 1}
                      </Text>
                    </Group>
                    <Text size="lg" fw={700} c="blue">
                      {competitor.mentionCount} mentions
                    </Text>
                  </Card>
                ))}
            </SimpleGrid>
          </Paper>
        )}
    </Stack>
  );
}
